import type { AppContext } from 'next/app';
import isNil from 'lodash/isNil';
import every from 'lodash/every';
import map from 'lodash/map';
import includes from 'lodash/includes';
import keys from 'lodash/keys';
import Cookie from 'cookie';
import { v4 as uuid } from 'uuid';
import has from 'lodash/has';
import find from 'lodash/find';
import moment from 'moment';
import round from 'lodash/round';
import get from 'lodash/get';
import omit from 'lodash/omit';
import startCase from 'lodash/startCase';
import { getClientIp } from '@supercharge/request-ip';

import { captureException } from '@jerry-serverless/next-sentry';
import { getInternalSource, getPartnerPublisher, getPartnerReferrer } from '@jerry-serverless/analyticslib/partner';
import { COGNITO_ID_TOKEN_HEADER_NAME } from '@jerry-serverless/graphql-helpers/apollo/client';
import {
  JA_SESSION_ID_COOKIE_NAME,
  JERRY_ANALYTICS_ENGAGEMENT_ID_COOKIE_NAME,
  JERRY_ANALYTICS_DEVICE_ID_COOKIE_NAME as COOKIE_DEVICE_ID,
} from '@jerry-serverless/ui-analytics/lib/scripts/jerry-analytics-script';
import { JERRY_ANALYTICS_INTERNAL_SOURCE } from '@jerry-serverless/analyticslib/common';
import { IncomeFrequencyType } from '@jerry-serverless/enums/user';
import { NotInsuredReason } from '@jerry-serverless/enums/prior_insurance';
import { SignupChallengeStep } from '@jerry-serverless/enums/user_challenge';
import { FlowType } from '@jerry-serverless/types';

import type { SignupParams } from '../types/signup_params';
import { TrafficSchoolStatus } from '../types/traffic_school.enum';
import type { AppData, QuoteAppData, SelectedServiceType } from '../types/app_data';
import { AppStepSection } from '../types/app_data';
import { parseSignupParams, setSignupParams, parseSignupParamsNoCache } from '../types/signup_params';
import type { User } from '../apollo/graphql/user.fragment';
import type { Car } from '../types/car';
import { RelationToUser } from '../types/driver';
import { isInQuotePages } from '../steps';
import { CarsStep } from '../steps/onboarding/cars.step';
import type { UserWithQuotesType } from '../apollo/graphql/quote.query';
import type { RefiUserVehicle } from '../apollo/graphql/refi_qualified.query';
import { parseError } from '../apollo/index';
import { ConfirmInfoSignupParamsFields } from '../apollo/graphql/challenge.mutation';

import { mockDataServer } from './e2e_test';
import { getFeatureData } from './enabled_features';
import {
  PARTIAL_VIN_SUFFIX,
  PARTIAL_VIN_OLD_SUFFIX,
  mapCarUsageDBValueToFrontendVal,
  getNearestDailyMileage,
} from './car';
import { PARTNER_API_USER_TOKEN, PARTNER_API_PARTNER_RAW_DATA_TOKEN } from './url';
import { isMobile, getMobileOperatingSystem } from './mobile';
import { isFromSeoFunnel } from './seo';
import { createGrowthBookFromAppData } from './analytics/growthbook';
import { trackEvent } from './analytics';
import { getChannel, getChannelDetectorParams } from './channel';
import { ChannelTypes } from './channel/const';
import { getFeatureValues } from './enabled_features/utils';
import { filterEnabledTestsFeatures } from './enabled_features/ab_tests';
import { autoSelectedServicesByReferer } from './service_split';
import { getLoginUser, getUserFromPartner, isInE2ETestEnv, createPartnerUserAndQuote } from './app_user';
import { getLoginChallengeData } from './login_challenge';
import { getInviteV3Data } from './invite_v3';
import { isValidOccupation } from './occupations';
import { isValidEducation } from './education';

// Mobile user agent string.
const MOBILE_APP_USER_AGENT = 'AppName/jerrym';

// Prefill quoting variation cookie name.
const PREFILL_QUOTING_VARIATION_NAME = 'prefill_quoting_variation';

// Disqualified max vehicle mileage for Refi
const REFI_MAX_VEHICLE_MILEAGE = 100000;
// Disqualified min monthly income(cents) for Refi
const REFI_MIN_MONTHLY_INCOME = 200000;

/**
 * Set default 'shopFor' value for facebook/cbautogroup traffics.
 * @param shopFor
 */
const getDefaultShopFor = (shopFor?: string) => {
  const defaultShopFor = { home: false, renters: false, condo: false, umbrella: false };

  // 'auto' param will be used to indiciate default 'shopfor' values.
  if (shopFor && shopFor !== 'auto') {
    return { ...defaultShopFor, [shopFor]: true };
  }

  return defaultShopFor;
};

/**
 * Extract partial vin.
 * @param car
 * @returns
 */
const getPartialVin = (car: Car): string | null => {
  if (!car.partialVin) {
    return null;
  }

  // If car's year is less than 1981, append six 1s. Otherwise append seven 1s.
  return car.year && parseInt(car.year, 10) < 1981
    ? `${car.partialVin}${PARTIAL_VIN_OLD_SUFFIX}`
    : `${car.partialVin}${PARTIAL_VIN_SUFFIX}`;
};

/**
 * Returns true, if user is `Jerry2UserQuotesType` type
 * @param user
 * @returns
 */
const isUserWithQuotesType = (user: User | UserWithQuotesType): user is UserWithQuotesType => {
  return has(user, 'stage') && has(user, 'bundledQuotes');
};

/**
 * Merges user data from the `viewer` graphql query into the app data.
 */
export const mergeViewerQueryDataInAppData = (
  user: User | UserWithQuotesType | null,
  appData: AppData,
  // temporary solution to drivers being overwritten - should be removed once driver changes are no longer saved in the car page
  preferAppDataDrivers?: boolean,
  // use the route to determine if the user is in the quote section
  route?: string
): AppData | QuoteAppData => {
  if (!user) return appData;

  const signupParams = parseSignupParams(user.signupParams, appData);

  let stepSection = appData.stepSection;

  if (user.isSigned) {
    stepSection = AppStepSection.Onboarding;
  }

  if (appData.signupChallengeStep && appData.signupChallengeStep !== SignupChallengeStep.Finished) {
    stepSection = AppStepSection.Challenge;
  } else if (signupParams.validated) {
    stepSection = isInQuotePages(route ?? '') ? AppStepSection.Quotes : AppStepSection.Finished;
  }

  const shouldUsePreOnboardingFields = stepSection === AppStepSection.Challenge;

  let isHomeowner = !isNil(user.isHomeowner) ? user.isHomeowner : undefined;
  if (shouldUsePreOnboardingFields) {
    const signupInput = getLoginChallengeData();
    if (signupInput) {
      // Fields of User
      isHomeowner = signupInput.isHomeowner;
      // signupParams
      ConfirmInfoSignupParamsFields.forEach((field) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        signupParams[field] = signupInput[field] as any;
      });
    }
  }

  // user is in invite v3 flow
  if (appData.isOnboardingLite && (appData.inviteV3DataInParams || appData.inviteV3DataInCookies)) {
    stepSection = AppStepSection.CreateAccount;
  }

  const selectedServiceFromSignupParams = {
    insurance: signupParams?.insuranceService,
    garage: signupParams?.garageService,
    dwep: signupParams?.dwepService,
    refi: signupParams?.refiService,
  };
  let selectedService;
  if (user.isOnboardingLite) {
    selectedService = {
      ...appData.selectedService,
      dwep: true,
    };
  } else if (user.isSigned) {
    const isEmptyService = every(selectedServiceFromSignupParams, isNil);
    const isChallengeSection = stepSection === AppStepSection.Challenge;

    selectedService = {
      ...selectedServiceFromSignupParams,
      /**
       * If user is on the challenge flow, and has not selected any service (store in signupParams),
       * we should treat the user as 'insurance' selected.
       */
      insurance: isEmptyService && isChallengeSection ? true : selectedServiceFromSignupParams.insurance,
    };
  } else {
    selectedService = appData.selectedService;
  }

  const isFromMAL = !!user.isFromMAL;
  // Challenge flow is common, it has its own navigation
  const sectionFlowType =
    stepSection === AppStepSection.Challenge ? appData.flowType : signupParams?.flowType || appData.flowType;
  return {
    ...appData,
    stepSection,
    signupParams,
    id: user.id,
    isFromMAL,
    ...(isFromMAL && { specifiedService: 'refi' }),
    name: user.name,
    phone: user.phone,
    phoneVerified: user.phoneVerified,
    email: user.email,
    isSigned: user.isSigned,
    userFieldsUpdatedAt: user.fieldsUpdatedAt,
    isHomeowner,
    telematics: !isNil(user.telematics) ? user.telematics : undefined,
    dateOfBirthMasked: !isNil(user.dateOfBirthMasked) ? user.dateOfBirthMasked.value : undefined,
    age: !isNil(user.age) ? user.age : undefined,
    address: !isNil(user.address) ? user.address : undefined,
    enableLDU: !isNil(user.enableLDU) ? user.enableLDU : undefined,
    driverID: !isNil(user.driverID) ? user.driverID : undefined,
    referralSource: !isNil(user.referralSource) ? user.referralSource : undefined,
    flowType: sectionFlowType,
    isOnboardingLite: user.isOnboardingLite,
    isBeyondDrivingAge: user.isBeyondDrivingAge,
    cars: map(user.cars?.edges, ({ node: car }) => {
      const isPartialVin = !car.vinMasked && !!car.partialVin;
      const partialVinFormatted = isPartialVin ? getPartialVin(car) : null;
      const vin = car.vinMasked?.value || (isPartialVin ? partialVinFormatted : null);
      const usageMapped = mapCarUsageDBValueToFrontendVal(car);
      return {
        ...car,
        isPartialVin,
        partialVin: partialVinFormatted,
        usage: usageMapped[0],
        business: usageMapped[1],
        vin,
        annualMileage: car.annualMileage ? getNearestDailyMileage(car.annualMileage) * 365 : undefined,
        additionalInfo: car.additionalInfo ? omit(car.additionalInfo, '__typename') : undefined,
      };
    }),
    drivers: preferAppDataDrivers
      ? appData.drivers
      : map(user.drivers?.edges, ({ node: driver }) => ({
          ...driver,
          additionalInfo: driver.additionalInfo ? omit(driver.additionalInfo, '__typename') : undefined,
        })),
    multipleCarsAndDrivers: signupParams.multipleCarsAndDrivers, // First set in backend UpdateUser5
    mlCoverageEnabled: !isNil(user.mlCoverageEnabled) ? user.mlCoverageEnabled : undefined,
    hasCarLoan: signupParams.hasCarLoan,
    hasMinNumberCarLoanPayments: signupParams.hasMinNumberCarLoanPayments,
    carLoanBalance: signupParams.carLoanBalance,
    carLoanMonthlyPayment: signupParams.carLoanMonthlyPayment,
    selectedService,
    serviceSplitSelectedService: appData.serviceSplitSelectedService || selectedService,
    averageCarPolicyPriceByState:
      user.averageCarPolicyPriceByState || appData.averageCarPolicyPriceByState || undefined,
    ...(isUserWithQuotesType(user)
      ? {
          stage: user.stage,
          isQuotingInProgress: user.isQuotingInProgress,
          currentQuotingProgress: user.currentQuotingProgress,
          currentBatchFailedReasons: user.currentBatchFailedReasons,
          currentBatchFinishedTiers: user.currentBatchFinishedTiers,
          currentBatchCheckedCarriersCount: user.currentBatchCheckedCarriersCount,
          bundledQuotes: user.bundledQuotes,
          activePolicies: user.activePolicies,
        }
      : {}),
  };
};

/**
 * Give an AppData, return a new AppData with `tempCars` and `selectedCars` being updated according to `cars`.
 */
export const updateTempCarsAndSelectedCars = (appData: AppData): AppData => {
  const tempCars = appData.cars?.map((car) => ({ ...car, selected: !car.isArchived }));
  const selectedCars = tempCars?.filter((car) => car.selected);
  return { ...appData, tempCars, selectedCars };
};

/**
 * Check url query object and 'userAgent' string for mobile web view params.
 * @param queryObj
 */
const getMobileAppParams = (
  queryObj: Record<string, string>,
  userAgent: string
): { mobileAppView: boolean; phone?: string } | undefined => {
  // Check if 'userAgent' string contains mobile app string.
  if (includes(userAgent, MOBILE_APP_USER_AGENT)) {
    return {
      mobileAppView: true,
      phone: queryObj['ph'],
    };
  }
  return undefined;
};

/**
 * Trigger partner create user and quote
 * @param context
 * @param cookieString
 * @param cookie
 * @param jasid
 * @param ptoken
 * @returns ABTest variation cookie from backend
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const startCreatePartnerUser = async (
  context: AppContext,
  cookieString: string,
  cookie: {
    [x: string]: string;
  },
  jasid: string,
  ptoken: string
): Promise<string | undefined> => {
  const clickID = context.ctx.req?.url?.match(/clickID=([^&]+)/)?.[1] || '';
  const cookieStringToSend = `${cookieString}; ${JA_SESSION_ID_COOKIE_NAME}=${jasid}`;
  let parsedErrMsg = '';
  let prefillQuotingVariationCookieValue: string | undefined;
  try {
    const rsp = await createPartnerUserAndQuote(context, cookieStringToSend, ptoken, clickID);
    if (rsp.data?.errors && rsp.data?.errors?.length > 0) {
      parsedErrMsg = rsp.data?.errors[0].message;
    }
    prefillQuotingVariationCookieValue = rsp.data?.prefillQuotingVariation;
  } catch (err) {
    parsedErrMsg = parseError(err) || '';
  }
  console.info(`[createPartnerUserAndQuote result] ${context.ctx.req?.url} :`, parsedErrMsg);
  return prefillQuotingVariationCookieValue;
};

/**
 * Returns the selected services and channel based on the referrer, cookie, and query object.
 */
const getServicesAndChannel = (args: {
  referrer: string;
  cookie: Record<string, string>;
  queryObj: Record<string, string>;
  context: AppContext;
}) => {
  const { referrer, cookie, queryObj, context } = args;
  // Auto select refi or garage services if user is from the seo pages
  const servicesByReferer = autoSelectedServicesByReferer(referrer);
  const showServiceSplit = keys(servicesByReferer).length > 0;

  const { utmSource } = getChannelDetectorParams({ cookie, query: context.ctx.query, referrer });
  const channel = getChannel({ cookie, query: context.ctx.query, referrer });

  /**
   * If there is service=xx from url query that matches the provided service,
   * directly go to the specified service flow.
   */
  const serviceFromQuery = queryObj['service'];
  const needConfirmService = queryObj['review'] === 'true';
  let flowType: FlowType | undefined;
  if (includes(Object.values(FlowType), queryObj['flowType'])) {
    flowType = queryObj['flowType'] as FlowType;
  }
  const selectedService: SelectedServiceType = {
    insurance: undefined,
    garage: undefined,
    dwep: undefined,
    refi: undefined,
    ...servicesByReferer,
  };
  let specifiedService;
  if (serviceFromQuery in selectedService) {
    selectedService[serviceFromQuery] = true;
    if (!needConfirmService) {
      specifiedService = serviceFromQuery;
    }
    void trackEvent('CreateAccountServiceSelected', {
      eventLabel: specifiedService,
      eventMetadata: {
        fromQueryString: true,
        flowType,
      },
    });
  } else if (channel !== ChannelTypes.direct && !showServiceSplit) {
    // Only direct channel will see the service split page
    selectedService.insurance = true;
  }

  return {
    selectedService,
    specifiedService,
    channel,
    utmSource,
    showServiceSplit,
    flowType,
  };
};

const getEstimateServiceFromReferer = (referer = '') => {
  if (referer.includes('car-repair/estimates')) {
    const match = referer.match(/(?<=car-repair\/estimates\/)[^/?]+/);
    if (match) {
      return startCase(match[0]); // 'battery-replacement' -> 'Battery Replacement'
    }
  }
  return undefined;
};

/**
 * Return the utm_source value from the signup query if it exists.
 *  Find utm_source:
 *    1. Check current query
 *    2. If `firstUrl` from cookie is a signup url, check its query
 */
const getUtmSourceFromSignupQuery = (
  queryObj: Record<string, string>,
  cookie: Record<string, string>
): string | undefined => {
  if (has(queryObj, 'utm_source')) {
    return queryObj['utm_source'];
  }
  const firstUrl = get(cookie, 'firstUrl');
  if (!firstUrl) {
    return;
  }

  try {
    const firstUrlObj = new URL(decodeURIComponent(firstUrl));
    if (/^\/signup($|\/)/.test(firstUrlObj.pathname)) {
      return firstUrlObj.searchParams.get('utm_source') ?? undefined;
    }
  } catch {
    return undefined;
  }
};

/**
 * Fetches server-side data required for rendering any of the signup pages.
 */
export const getAppData = async (context: AppContext): Promise<AppData> => {
  const cookieString = (context.ctx.req?.headers.jerrycookie || context.ctx.req?.headers.cookie || '') as string;
  const cookie = {
    ...Cookie.parse(context.ctx.req?.headers.cookie || ''),
    ...Cookie.parse((context.ctx.req?.headers.jerrycookie as string) || ''),
  };
  const referrer = (context.ctx.req?.headers.referer || '') as string;
  const userAgent = (context.ctx.req?.headers['user-agent'] || '') as string;
  const queryObj = context.ctx.query as Record<string, string>;
  const partner = getPartnerReferrer(queryObj, cookie, referrer) || undefined;
  const publisher = getPartnerPublisher(queryObj);
  const internalSource = getInternalSource(queryObj, cookie, referrer);
  const mobileAppParams = getMobileAppParams(queryObj, userAgent);
  const isMobileAgent = isMobile(userAgent);
  const isSeoFunnel = isFromSeoFunnel(queryObj);
  const mobileOS = getMobileOperatingSystem(userAgent);
  const clientIP = getClientIp(context.ctx.req);

  const shopFor = queryObj['shopfor'];
  const trafficSchoolStatus = queryObj['trafficSchool'] === '1' ? TrafficSchoolStatus.ENTERED : null;

  const { selectedService, specifiedService, channel, utmSource, showServiceSplit, flowType } = getServicesAndChannel({
    referrer,
    cookie,
    queryObj,
    context,
  });

  let jasid = cookie[JA_SESSION_ID_COOKIE_NAME];
  if (!cookie[JA_SESSION_ID_COOKIE_NAME]) {
    jasid = uuid();
  }

  let appData: AppData = {
    stepSection: AppStepSection.CreateAccount,
    mobileAppView: mobileAppParams?.mobileAppView,
    phone: mobileAppParams?.phone,
    isMobileAgent,
    mobileOS,
    partner,
    publisher,
    utoken: queryObj[PARTNER_API_USER_TOKEN],
    ptoken: queryObj[PARTNER_API_PARTNER_RAW_DATA_TOKEN],
    internalSource,
    isSeoFunnel,
    specifiedService,
    selectedService,
    channel,
    utmSource,
    utmSourceFromSignupQuery: getUtmSourceFromSignupQuery(queryObj, cookie),
    showServiceSplit,
    sessionId: jasid,
    clientIP,
    ptokenForTrack: queryObj[PARTNER_API_PARTNER_RAW_DATA_TOKEN],
    flowType,
    garageOnlyFlowPageTempData: {
      /** If from https://getjerry.com/car-repair/estimates/battery-replacement/ the estimate service is estimateService */
      estimateService: getEstimateServiceFromReferer(referrer),
      carValueAccepted: flowType === FlowType.CarValue,
    },
  };

  let user: User | null = null;
  let sp: SignupParams | null = null;
  let signupChallengeStep: SignupChallengeStep | null = null;

  // Jerry session cookie.
  // Note: If partner user is valid and can land directly on onboarding steps,
  // we have to sync the jerry cookie value so session persists between SSR and frontend.
  let jerryCookie = '';
  let jerryauth = '';
  let cognitoIdToken = '';
  const isSignupFromPartner = (appDataPara: AppData) => !!appDataPara.utoken || !!appDataPara.ptoken;
  let prefillQuotingVariationCookieValue = cookie[PREFILL_QUOTING_VARIATION_NAME]; // prefill_quoting_variation = "3f856f46-b665-4f86-a9be-797cfdb76ad8_0"
  let prefillQuotingVariation: number | undefined;

  // app use `jerrycookie` but only for page request, so set cookie here
  if (context.ctx.req?.headers.jerrycookie) {
    jerryCookie = context.ctx.req?.headers.jerrycookie as string;
  }
  if (context.ctx.req?.headers[COGNITO_ID_TOKEN_HEADER_NAME]) {
    cognitoIdToken = `${COGNITO_ID_TOKEN_HEADER_NAME}=${context.ctx.req?.headers[COGNITO_ID_TOKEN_HEADER_NAME]}; path=/`;
  }

  // Create the server side GrowthBook
  let growthbook = createGrowthBookFromAppData(appData);
  await growthbook.loadFeatures();
  appData.growthBookFeatureData = filterEnabledTestsFeatures(growthbook.getFeatures());

  // Set default option to insurance by default.
  const selectServiceValues = Object.values(selectedService || {});
  if (selectServiceValues.every((value) => !value)) {
    selectedService.insurance = true;
    appData.selectedService = selectedService;
  }

  try {
    if (isInE2ETestEnv(cookie)) {
      user = mockDataServer;
    } else {
      ({ user, signupChallengeStep } = await getLoginUser(context));

      if (!user && isSignupFromPartner(appData)) {
        if (appData.ptoken) {
          // Trigger create partner user and quoting
          // Users are splitted inner backend api. Test group2, force to go through normal signup even though user comes from partner
          const newCookieValue = await startCreatePartnerUser(context, cookieString, cookie, jasid, appData.ptoken);
          prefillQuotingVariationCookieValue = newCookieValue ?? prefillQuotingVariationCookieValue;
        }
        const result = await getUserFromPartner(
          context,
          appData.utoken || '',
          appData.ptoken || '',
          cookieString,
          jasid
        );
        user = result.user;
        jerryCookie = result.jerryCookie || jerryCookie;
        jerryauth = result.jerryauth || jerryauth;
        appData.partnerPrefillData = result.partnerPrefillData;
        if (result.partnerPrefillData?.averageCarPolicyPriceByState) {
          appData.averageCarPolicyPriceByState = result.partnerPrefillData?.averageCarPolicyPriceByState;
        }

        // Try to reuse prefill quoting ABTest variation
        if (appData.ptoken && !prefillQuotingVariationCookieValue && !!result.user?.signupParams) {
          sp = parseSignupParamsNoCache(result.user.signupParams);
          if (!sp.validated && !isNil(sp.partnerSignupVariation) && !isNil(sp.partnerSignupSessionID)) {
            jasid = sp.partnerSignupSessionID; // Use previous sessionID
            prefillQuotingVariationCookieValue = `${jasid}_${sp.partnerSignupVariation}`; // Use previous variation
            appData.sessionId = jasid; // SessionID changed, update
          }
        }
      }
    }

    if (prefillQuotingVariationCookieValue) {
      const extractValue = prefillQuotingVariationCookieValue.replace(`${jasid}_`, '');
      if (['0', '1', '2', '3'].includes(extractValue)) {
        prefillQuotingVariation = parseInt(extractValue);
      } else {
        prefillQuotingVariation = undefined;
      }
    }

    if (!sp && user?.signupParams) {
      sp = parseSignupParams(user.signupParams, appData);
    }

    appData = {
      ...appData,
      signupChallengeStep,
      inviteV3DataInCookies: getInviteV3Data(cookie),
    };

    // update GrowthBook
    growthbook = createGrowthBookFromAppData({
      ...appData,
      id: user?.id,
      phone: user?.phone,
      address: !isNil(user?.address) ? user.address : undefined,
    });
    appData.growthBookFeatureData = filterEnabledTestsFeatures(growthbook.getFeatures());

    appData = mergeViewerQueryDataInAppData(user, appData, false, context.ctx.pathname);
    // update featureData by checking growthbook
    appData.featureData = getFeatureValues({
      appData,
      queryObj,
      user,
      partnerSignup: isSignupFromPartner(appData),
      growthbook,
      prefillQuotingVariation,
    });
  } catch (err) {
    appData.stepSection = AppStepSection.CreateAccount;
    captureException(err);
  }

  const { featureCookieState, ...featureData } = getFeatureData({ queryObj, cookieObj: cookie });

  /**
   * If user is from partner API funnel, save the initial data that was pulled in.
   * This is to keep step orders in sync with 'skippable' steps('address', 'dob', 'insured', 'claims', 'violations').
   * The 'User' data gets updated during the funnel which can cause following discrepancy;
   * 1) User lands on 'insured' step.
   * 2) User selects 'I'm insured'.
   * 3) User data gets updated with insured value.
   * 4) The 'insured' step is now 'skippable' since user is from partner API funnel + data exists.
   * 5) Step routing causes error since current 'insured' step is not part of 'showable' step list.
   * To remove above scenario, we are saving the inital data that got passed in from server side and check if pages are 'skippable'.
   */
  if (appData.signupParams?.partnerSignup) {
    const primaryDriver = find(
      appData.drivers,
      (driver) => !driver.isArchived && driver.relationToUser === RelationToUser.Insured
    );
    const spouseDriver = find(
      appData.drivers,
      (driver) => !driver.isArchived && driver.relationToUser === RelationToUser.Spouse
    );

    appData.partnerSkippableData = {
      isAutoInsured: undefined,
      autoInsuredLength: undefined, // Skip 'autoInsuredLength' step
      claims: undefined,
      violations: undefined,
      dateOfBirthMasked: undefined,
      address: undefined,
      cars: appData.cars,
      hasOccupationPrimaryDriverID: isValidOccupation(primaryDriver?.occupation) ? primaryDriver?.id : undefined,
      hasOccupationSpouseDriverID: isValidOccupation(spouseDriver?.occupation) ? spouseDriver?.id : undefined,
      multiplecarsdrivers: appData.signupParams?.multipleCarsAndDrivers,
      homeOwnership: appData.signupParams?.homeOwnership, // https://app.asana.com/0/1202292931304613/1208173814339814/f
      education: isValidEducation(primaryDriver?.education) ? primaryDriver?.education || undefined : undefined,
      email: undefined,
    };
  }

  /**
   * If user is from facebook and we have 'shopFor' url query param,
   * set default 'shopFor' signup params.
   */
  if ((appData.partner?.partner === 'facebook' || appData.partner?.partner === 'cbautogroup') && shopFor) {
    if (shopFor) {
      appData.signupParams = setSignupParams(appData.signupParams, { shopFor: getDefaultShopFor(shopFor) });
    }
  }

  if (trafficSchoolStatus) {
    appData.signupParams = setSignupParams(appData.signupParams, {
      trafficSchoolStatus,
    });
    appData.selectedService = { insurance: true, garage: false, dwep: false, refi: false };
  }

  // Set cookie can only set one key/value per header, so here need multi headers (using array)
  // Ref: https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Set-Cookie
  const cookiesToSet = [jerryCookie, jerryauth, cognitoIdToken];
  if (featureCookieState) {
    cookiesToSet.push(featureCookieState);
  }
  if (cookie[COOKIE_DEVICE_ID]) {
    // Sync device ID into cookie (if not, app webview may lose it)
    cookiesToSet.push(`${COOKIE_DEVICE_ID}=${cookie[COOKIE_DEVICE_ID]}`);
  }
  cookiesToSet.push(`${JA_SESSION_ID_COOKIE_NAME}=${jasid};path=/`);
  if (prefillQuotingVariationCookieValue) {
    cookiesToSet.push(`${PREFILL_QUOTING_VARIATION_NAME}=${prefillQuotingVariationCookieValue};path=/`);
  }

  // add engagement id to cookie
  const engagementID = cookie[JERRY_ANALYTICS_ENGAGEMENT_ID_COOKIE_NAME] || uuid();
  cookiesToSet.push(`${JERRY_ANALYTICS_ENGAGEMENT_ID_COOKIE_NAME}=${engagementID};path=/`);

  if (internalSource && !cookie[JERRY_ANALYTICS_INTERNAL_SOURCE]) {
    cookiesToSet.push(`${JERRY_ANALYTICS_INTERNAL_SOURCE}=${internalSource}`);
  }

  appData = {
    ...appData,
    ...featureData,
  };

  // Update features state cookie
  context.ctx.res?.setHeader('Set-Cookie', cookiesToSet);

  if (appData.featureData) {
    appData.featureData.prefillQuotingVariation = prefillQuotingVariation; // Cookie variation value
    // appData.featureData.prefillQuotingOn = isNil(prefillQuotingVariation) ? undefined : false;
    // // If cookie is Variation 1,2, double check prefilled user info
    // if (!!user?.id && !!appData.signupParams?.partnerSignup && (prefillQuotingVariation || 0) > 0) {
    //   appData.featureData.prefillQuotingOn = true;
    // }
  }

  // For bundle opt-in to initiate signup params
  if (!appData.signupParams?.shopFor) {
    appData.signupParams = setSignupParams(appData.signupParams, { shopFor: getDefaultShopFor() });
  }

  // Construct app data.
  return appData;
};

/**
 * Get car that user selected to refinance.
 * @param appData
 * @returns
 */
const getSelectedCar = (appData: AppData): RefiUserVehicle | undefined => {
  const vehicles = appData.refiQualifiedData?.vehicles || [];
  if (appData.selectedCar) {
    return find(
      vehicles,
      (car) =>
        car.year?.toString() === appData.selectedCar?.year &&
        car.make === appData.selectedCar?.make &&
        car.model === appData.selectedCar?.model &&
        car.submodel === appData.selectedCar?.submodel
    );
  }

  const tempCars = appData.tempCars || [];
  if (appData.selectedService?.refi && tempCars.length === 1) {
    return find(
      vehicles,
      (car) =>
        car.year?.toString() === tempCars[0].year &&
        car.make === tempCars[0].make &&
        car.model === tempCars[0].model &&
        car.submodel === tempCars[0].submodel
    );
  }
};

/**
 * Check the user and vehicle are qualified for refinance.
 * @param appData
 */
export const isRefiQualified = (appData: AppData): boolean => {
  // Onboarding.
  if (appData.isSigned) {
    const isAutoInsured = appData.signupParams?.isAutoInsured;
    const notInsuredReason = appData.signupParams?.notInsuredReason;
    // If user is not insured and not expired last 30 days, knockout.
    if (isAutoInsured === false && notInsuredReason !== NotInsuredReason.EXPIRED_LAST_30_DAYS) {
      void trackEvent('NotInsuredOrExpired');
      return false;
    }

    const isUserQualified = appData.refiQualifiedData?.isRefiQualified;
    if (!isUserQualified) {
      void trackEvent('UserIneligible', {
        eventMetadata: {
          userId: appData.id || '',
          dob: appData.age || '',
          state: appData.address?.state || '',
        },
      });
      return false;
    }

    const selectedCar = getSelectedCar(appData);
    if (selectedCar && !selectedCar.isRefiQualified) {
      void trackEvent('CarIneligible', {
        eventMetadata: {
          carId: selectedCar.id,
          year: selectedCar.year || '',
          make: selectedCar.make || '',
          model: selectedCar.model || '',
          submodel: selectedCar.submodel || '',
        },
      });
      return false;
    }
    return true;
  }

  // Create account.
  if (appData.selectedCar) {
    const year = appData.selectedCar.year;
    // Knockout if vehicle age is greater than 10 years.
    if (!year || moment().diff(moment(year, 'YYYY'), 'years') > 10) {
      void trackEvent('CarIneligible', {
        eventMetadata: {
          carId: appData.selectedCar.id,
          year: appData.selectedCar.year || '',
          make: appData.selectedCar.make || '',
          model: appData.selectedCar.model || '',
          submodel: appData.selectedCar.submodel || '',
        },
      });
      return false;
    }
    return true;
  }

  const tempCars = appData.tempCars || [];
  if (appData.selectedService?.refi && tempCars.length === 1) {
    const year = tempCars[0].year;
    // Knockout if vehicle age is greater than 10 years.
    if (!year || moment().diff(moment(year, 'YYYY'), 'years') > 10) {
      void trackEvent('CarIneligible', {
        eventMetadata: {
          carId: tempCars[0].id,
          year: tempCars[0].year || '',
          make: tempCars[0].make || '',
          model: tempCars[0].model || '',
          submodel: tempCars[0].submodel || '',
        },
      });
      return false;
    }
    return true;
  }

  // User has not selected a car for refinance yet.
  return true;
};

/**
 * Additional check has a car loan and has the minimum number payments.
 * @param appData
 * @returns
 */
export const isRefiQualifiedWithMinimumPayments = (appData: AppData): boolean => {
  // Maybe from onboarding or create account.
  const hasMinNumberCarLoanPayments =
    appData.hasMinNumberCarLoanPayments || appData.signupParams?.hasMinNumberCarLoanPayments;
  if (!hasMinNumberCarLoanPayments) {
    void trackEvent('NoMinNumberPayments', {
      eventMetadata: {
        userId: appData.id || '',
      },
    });
  }

  return isRefiQualified(appData) && hasMinNumberCarLoanPayments !== false;
};

/**
 * Additional check vehicle the estimated mileage.
 * @param appData
 * @returns
 */
export const isRefiQualifiedWithEstimatedMileage = (appData: AppData): boolean => {
  if (appData.selectedCar) {
    const selectedCar = find(appData.selectedCars, (car) => car.id === appData.selectedCar?.id);
    // Car odometer is eligible if it is less than or equal to 100000.
    const eligibleOdometer = !selectedCar?.odometer || selectedCar.odometer <= REFI_MAX_VEHICLE_MILEAGE;
    if (!eligibleOdometer) {
      void trackEvent('HighEstimatedMileage', {
        eventMetadata: {
          carId: selectedCar?.id || '',
          odometer: selectedCar?.odometer || 0,
        },
      });
    }
    // Knockout if estimated mileage is greater than 100000.
    return isRefiQualifiedWithMinimumPayments(appData) && eligibleOdometer;
  }

  const selectedCars = appData.selectedCars || [];
  if (appData.selectedService?.refi && selectedCars.length === 1) {
    // Car odometer is eligible if it is less than or equal to 100000.
    const eligibleOdometer = !selectedCars[0].odometer || selectedCars[0].odometer <= REFI_MAX_VEHICLE_MILEAGE;
    if (!eligibleOdometer) {
      void trackEvent('HighEstimatedMileage', {
        eventMetadata: {
          carId: selectedCars[0].id,
          odometer: selectedCars[0].odometer || 0,
        },
      });
    }
    // Knockout if estimated mileage is greater than 100000.
    return isRefiQualifiedWithMinimumPayments(appData) && eligibleOdometer;
  }

  // User has not selected a car for refinance yet.
  return true;
};

/**
 * Additional check has auto insured or not and not insured reason.
 * @param appData
 * @returns
 */
export const isRefiQualifiedWithAutoInsured = (appData: AppData): boolean => {
  const isAutoInsured = appData.signupParams?.isAutoInsured;
  const notInsuredReason = appData.signupParams?.notInsuredReason;
  if (isAutoInsured === false && notInsuredReason !== NotInsuredReason.EXPIRED_LAST_30_DAYS) {
    void trackEvent('NotInsuredOrExpired');
  }
  // Knockout when user has not insured and give a reason that is not expired last 30 days.
  return (
    isRefiQualifiedWithEstimatedMileage(appData) &&
    (isAutoInsured !== false || notInsuredReason === NotInsuredReason.EXPIRED_LAST_30_DAYS)
  );
};

/**
 * Additional check user's gross income.
 * @param appData
 * @returns
 */
export const isRefiQualifiedWithGrossIncome = (appData: AppData): boolean => {
  const income = appData.signupParams?.income;
  if (!income?.frequency || !income?.amount) {
    return false;
  }
  let monthlyIncome = 0;
  switch (income.frequency) {
    case IncomeFrequencyType.Hourly:
      monthlyIncome = round(income.amount * (income?.averageHoursWeekly ?? 40) * (52 / 12));
      break;
    case IncomeFrequencyType.Biweekly:
      monthlyIncome = income.amount * (26 / 12);
      break;
    case IncomeFrequencyType.Monthly:
      monthlyIncome = income.amount;
      break;
    case IncomeFrequencyType.Annually:
      monthlyIncome = income.amount / 12;
  }
  // Monthly income is eligible if it is greater than 200000(cents).
  const eligibleIncome = monthlyIncome > REFI_MIN_MONTHLY_INCOME;
  if (!eligibleIncome) {
    void trackEvent('LowGrossIncome', {
      eventMetadata: {
        userId: appData.id || '',
        monthlyIncome,
      },
    });
  }
  // Knockout if monthly income is less or equal than 200000(cents).
  return isRefiQualifiedWithAutoInsured(appData) && eligibleIncome;
};

/**
 * Check if we should load selected cars from user cars
 *
 * If there is no cars step in onboarding section, the `appData.selectedCars` would be empty.
 * In such case, in order to show the garage cross-sell steps properly, we need to load the selected cars from user cars.
 */
export const shouldLoadSelectedCarsFromUserCars = (appData: AppData): boolean => {
  return (
    // in onboarding section
    !!appData.isSigned &&
    // no cars step
    !CarsStep.canShow(appData) &&
    // garage service is not selected, i.e. garage cross-sell steps should be shown
    !appData.serviceSplitSelectedService?.garage
  );
};

/**
 * Check one car has setup maintenance reminders
 */
export const carHasSetupMaintenanceReminders = (appData: AppData, carId?: string): boolean => {
  if (!carId) return false;
  return appData.MaintenanceReminderSettledCars?.has(carId) ?? false;
};

/**
 * Get garage cars that have not set up maintenance reminders
 */
export const getGarageCarsToSetupMaintenanceReminders = (appData: AppData): Car[] => {
  const selectedCars = appData.selectedCars || [];
  return selectedCars.filter((car) => !appData.MaintenanceReminderSettledCars?.has(car.id));
};

/**
 * Check if user has garage cars that has not set up maintenance reminders
 */
export const hasGarageCarsToSetupMaintenanceReminders = (appData: AppData): boolean => {
  return getGarageCarsToSetupMaintenanceReminders(appData).length > 0;
};
